import {
  BoundMultiSelectField,
  BoundRadioGroupField,
  BoundSelectField,
  BoundSelectFieldProps,
  Css,
  HasIdAndName,
  Tokens,
  Tooltip,
} from "@homebound/beam";
import { FieldState, FormObjectState, ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { ReactNode, useEffect, useMemo } from "react";
import { BannerNotice, Loading } from "src/components";
import { ArchivedTag } from "src/components/ArchivedTag";
import { UnitsOfMeasureBoundSelectField } from "src/components/autoPopulateSelects/UnitsOfMeasureBoundSelectField";
import { BoundAiNumberField, BoundAiSelectField } from "src/components/formFields/ai";
import { AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import { parseAiNamedOption } from "src/components/formFields/ai/utils/bindAiProposal";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType, Maybe, SaveTakeoffLineItemInput, TakeoffLineItemAction } from "src/generated/graphqlTypes";
import {
  AddEditItemModal_GlobalPlanTaskFragment,
  AddEditItemModal_ItemFragment,
  AddEditItemModal_LocationFragment,
  type AddEditItemModal_MaterialVariant_SelectedFragment,
  type AddEditItemModal_MvFragment,
  AddEditItemModal_ReadyPlanFragment,
  AddEditItemModal_TliFragment,
  AddEditItemModal_UomFragment,
  useAddEditItemModal_GetReadyPlanQuery,
  useAddEditItemModal_GetTliQuery,
  useAddEditItemModal_GlobalPlanTasksLazyQuery,
} from "src/routes/libraries/plan-package/takeoffs/components/AddItems/AddEditItemModal.generated";
import { StoreApi } from "zustand";
import { SaveTliResult, TakeoffsStoreState } from "../../TakeoffsManagerContext";
export function PublishedTakeoffBanner() {
  return (
    <BannerNotice message="This takeoff has been published and cannot be edited" variant="form" icon="alertInfo" />
  );
}

export type AddEditItemObjectState = SaveTakeoffLineItemInput & {
  materialVariant: Maybe<MaterialVariantFormValue>;
  unitOfMeasure: Maybe<AddEditItemModal_UomFragment>;
  item: Maybe<AddEditItemModal_ItemFragment>;
  location: Maybe<AddEditItemModal_LocationFragment>;
  newLocationPath?: Maybe<string>;
};

/**
 * The AI's pending proposals the line fields show in place: Qty, UoM and Task on any AI line, plus Location and
 * Material on a line the AI created, which was saved with them.
 */
export type TakeoffAiProposals = Partial<
  Record<"quantity" | "unitOfMeasure" | "task" | "location" | "materialVariant", ObjectState<AiFieldProposalFormValue>>
>;

// Search selections have no attribute editor data; existing placeholder variants do.
type MaterialVariantFormValue = AddEditItemModal_MaterialVariant_SelectedFragment &
  Partial<Pick<AddEditItemModal_MvFragment, "deletedAt" | "effectiveMADs" | "materialAttributeValues">>;

export const addItemConfig: ObjectConfig<AddEditItemObjectState> = {
  id: { type: "value" },
  itemId: { type: "value" },
  materialVariantId: { type: "value" },
  quantity: { type: "value" },
  unitOfMeasureId: { type: "value", rules: [required] },
  locationId: { type: "value", rules: [required] },
  optionIds: { type: "value" },
  taskId: { type: "value" },

  action: { type: "value", rules: [required] },
  // The fully-assembled path (i.e., parent path + new feature path) which only gets set when adding a new feature
  newLocationPath: { type: "value" },
  // Also store the more detailed objects for easier access/logic needs
  location: { type: "value" },
  materialVariant: {
    type: "object",
    config: {
      id: { type: "value" },
      code: { type: "value" },
      displayName: { type: "value" },
      deletedAt: { type: "value" },
      listing: { type: "value" },
      materialAttributeValues: { type: "value" },
      effectiveMADs: { type: "value" },
    },
  },
  unitOfMeasure: { type: "value", rules: [required] },
  item: {
    type: "object",
    config: {
      unitOfMeasure: { type: "value" },
      planTaskMappings: { type: "value" },
      materialAttributeDimensions: {
        type: "list",
        config: {
          id: { type: "value" },
          name: { type: "value" },
          useInTakeoff: { type: "value" },
          unitOfMeasure: { type: "value" },
          values: {
            type: "list",
            config: {
              id: { type: "value" },
              code: { type: "value" },
              textValue: { type: "value" },
              dimension: { type: "value" },
            },
          },
          type: { type: "value" },
        },
      },
    },
  },
};

const addMaterialItemConfig: ObjectConfig<AddEditItemObjectState> = {
  ...addItemConfig,
  materialVariantId: { type: "value", rules: [required] },
};

export const addPlaceholderItemConfig: ObjectConfig<AddEditItemObjectState> = {
  ...addItemConfig,
  itemId: { type: "value", rules: [required] },
};

const addLaborItemConfig: ObjectConfig<AddEditItemObjectState> = {
  ...addItemConfig,
  taskId: { type: "value", rules: [required] },
};

export function useTakeoffRemovalsEnabled() {
  return useFeatureFlag(FeatureFlagType.TakeoffRemovals);
}

type ActionRadioFieldProps = {
  field: FieldState<Maybe<TakeoffLineItemAction>>;
  readOnly?: boolean;
  takeoffRemovalsEnabled: boolean;
};

export function ActionRadioField(props: ActionRadioFieldProps) {
  const { field, readOnly, takeoffRemovalsEnabled } = props;
  if (!takeoffRemovalsEnabled) return null;

  return (
    <BoundRadioGroupField
      label="Scope Action"
      layout="horizontal"
      field={field}
      disabled={readOnly}
      helperText="Specify whether this item should be added or removed when the selected option is chosen."
      options={[
        { label: "Add", value: TakeoffLineItemAction.Add },
        { label: "Remove", value: TakeoffLineItemAction.Remove },
      ]}
    />
  );
}

type CommonFieldsProps = {
  planPackageId: string;
  planPackageVersionId: string;
  readOnly: boolean;
  formState: ObjectState<AddEditItemObjectState>;
  takeoffRemovalsEnabled: boolean;
  disabled?: { uom?: ReactNode; quantity?: ReactNode; options?: ReactNode };
  /** Options as pills, labeled with code, name and location, per the V2 side panel's design */
  inPanel?: boolean;
  aiProposals?: TakeoffAiProposals;
};

/** Provides fields to the "Add Labor", "Add Placeholder", and "Add Construction" modals and the V2 side panel. */
export function CommonFields(props: CommonFieldsProps) {
  const {
    planPackageId,
    planPackageVersionId,
    readOnly,
    formState,
    disabled,
    takeoffRemovalsEnabled,
    inPanel,
    aiProposals,
  } = props;
  const { data: rpData } = useAddEditItemModal_GetReadyPlanQuery({
    variables: { readyPlanId: planPackageId, versionId: planPackageVersionId },
  });
  return (
    <>
      <div css={Css.df.jcsb.gap1.$}>
        <UnitsOfMeasureBoundSelectField
          label="UoM"
          field={formState.unitOfMeasureId}
          onSelect={(uomId, uom) => {
            formState.unitOfMeasureId.set(uomId);
            formState.unitOfMeasure.set(uom);
          }}
          disabled={disabled?.uom}
          aiProposal={aiProposals?.unitOfMeasure}
        />
        <Observer>
          {() => (
            <BoundAiNumberField
              label="Qty"
              field={formState.quantity}
              aiProposal={aiProposals?.quantity}
              disabled={
                disabled?.quantity ||
                (takeoffRemovalsEnabled && formState.action.value === TakeoffLineItemAction.Remove) ||
                (formState.unitOfMeasure.value &&
                  !formState.unitOfMeasure.value?.useQuantity &&
                  `${formState.unitOfMeasure.value!.name} does not uses Quantity`)
              }
            />
          )}
        </Observer>
      </div>
      {/* When in 'readOnly', do not show if it is not set. Otherwise, it results in only showing a label with no value and looks odd. */}
      {(!readOnly || formState.optionIds.value?.nonEmpty) && (
        <BoundMultiSelectField
          label="Options"
          field={formState.optionIds}
          options={rpData?.readyPlan?.options ?? []}
          getOptionLabel={(o) => o.displayName + (o.active ? "" : " (Archived)")}
          getOptionValue={(o) => o.id}
          getOptionMenuLabel={(o) =>
            inPanel ? <PanelOptionLabel option={o} /> : <ArchivedTag active={o.active}>{o.displayName}</ArchivedTag>
          }
          disabled={disabled?.options}
          // TODO: Beam gap (DS-453): no count badge, and read-only pills keep their remove buttons (hence `!readOnly`)
          withPillList={inPanel && !readOnly}
        />
      )}
    </>
  );
}

type PanelOptionLabelProps = { option: AddEditItemModal_ReadyPlanFragment["options"][number] };

/** Labels both the menu rows and the pills, since Beam uses one label for both. */
function PanelOptionLabel(props: PanelOptionLabelProps) {
  const { option } = props;
  const { displayCode, name, location, active } = option;
  return (
    <Tooltip title={location ? `${name} - ${location.name}` : name}>
      {/* One line that truncates, so a pill stays a pill */}
      <span css={Css.df.aic.gap1.mw0.wsnw.$}>
        <span css={Css.fs0.$}>{displayCode}</span>
        <span css={Css.smSb.truncate.mw0.$}>{name}</span>
        {location && <span css={Css.color(Tokens.OnSurfaceMuted).truncate.mw0.$}>{location.name}</span>}
        {!active && <span css={Css.red400.fs0.$}>(Archived)</span>}
      </span>
    </Tooltip>
  );
}

export type SelectedTask = { id: string | undefined; name: string | undefined };

export type OnTaskSelect = BoundSelectFieldProps<
  SelectedTask | AddEditItemModal_GlobalPlanTaskFragment | null,
  string
>["onSelect"];

type TakeoffTaskBoundSelectFieldProps = {
  selectedTask?: SelectedTask | null;
  readOnly: boolean;
  taskId: FieldState<Maybe<string>>;
  onSelect?: OnTaskSelect;
  required?: boolean;
  allowNone?: boolean;
  aiProposal?: ObjectState<AiFieldProposalFormValue>;
};

export function TakeoffTaskBoundSelectField(props: TakeoffTaskBoundSelectFieldProps) {
  const { selectedTask, readOnly, taskId, onSelect, required, allowNone, aiProposal } = props;
  const [loadTasks, { data: tasksData }] = useAddEditItemModal_GlobalPlanTasksLazyQuery({
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-only",
  });
  const tasks = tasksData?.globalPlanTasks.entities;
  // The AI names its task, so load the tasks now to show it, rather than when the select opens
  useEffect(() => {
    if (aiProposal) void loadTasks({ variables: { filter: { archived: false } } });
  }, [aiProposal, loadTasks]);
  const parser = useMemo(() => parseAiNamedOption(tasks ?? []), [tasks]);

  if (aiProposal) {
    return (
      <BoundAiSelectField<HasIdAndName, string>
        required={required}
        label="Task Allocation"
        field={taskId}
        nothingSelectedText="Please select a task"
        options={{
          // The AI select wants an id and name, which a saved line's task always has
          current: selectedTask?.id ? { id: selectedTask.id, name: selectedTask.name ?? "" } : undefined,
          load: () => loadTasks({ variables: { filter: { archived: false } } }),
          options: tasks,
        }}
        onSelect={onSelect}
        readOnly={readOnly}
        aiProposal={aiProposal}
        parser={parser}
        {...(allowNone && { unsetLabel: "None" })}
      />
    );
  }

  return (
    <BoundSelectField
      required={required}
      label="Task Allocation"
      field={taskId}
      nothingSelectedText="Please select a task"
      options={{
        current: selectedTask ?? undefined,
        load: () => loadTasks({ variables: { filter: { archived: false } } }),
        options: tasks,
      }}
      getOptionLabel={(o: any) => o.name}
      getOptionValue={(o: any) => o.id}
      onSelect={onSelect}
      readOnly={readOnly}
      {...(allowNone && { unsetLabel: "None" })}
    />
  );
}

export type AddItemModalProps = {
  tliId?: string;
  asNew?: boolean;
  planPackageId: string;
  takeoffsManagerStore: StoreApi<TakeoffsStoreState>;
  takeoffRemovalsEnabled?: boolean;
};

// I wonder why we're omitting id...
export type TliData = Omit<AddEditItemModal_TliFragment, "id"> & { id?: string };

export type AddEditItemModalDataViewProps = {
  initialTliData?: TliData;
  planPackageId: string;
  takeoffsManagerStore: StoreApi<TakeoffsStoreState>;
  takeoffRemovalsEnabled: boolean;
} & AddItemModalProps;

/**
 * Fetches the TLI to edit for the labor/placeholder/construction material modals.
 *
 * WrappedComponent will be one of those three modals.
 */
export function withGetTliQuery(WrappedComponent: React.FC<AddEditItemModalDataViewProps>) {
  return ({ tliId, asNew, takeoffRemovalsEnabled = false, ...rest }: AddItemModalProps) => {
    const versionId = rest.takeoffsManagerStore.getState().planPackage.version.id;
    const { loading, data: tliData } = useAddEditItemModal_GetTliQuery({
      variables: { id: tliId!, versionId },
      skip: !tliId,
    });
    if (loading) return <Loading />;
    const initialTliData: TliData | undefined =
      tliData && (asNew ? duplicateTliData(tliData.takeoffLineItem) : tliData.takeoffLineItem);
    return (
      <WrappedComponent {...rest} initialTliData={initialTliData} takeoffRemovalsEnabled={takeoffRemovalsEnabled} />
    );
  };
}

/** Pre-fills a new line from `tli`, as Duplicate does. */
export function duplicateTliData(tli: AddEditItemModal_TliFragment): TliData {
  return {
    ...tli,
    id: undefined,
    // A placeholder's MV follows from its item and attribute values
    ...(tli.isPlaceholderMaterial ? { materialVariant: undefined } : {}),
  };
}

const newAddEditItemFormDefaults: AddEditItemObjectState = {
  action: TakeoffLineItemAction.Add,
  materialVariant: undefined,
  unitOfMeasure: undefined,
  item: undefined,
  location: undefined,
};

/** A takeoff line's kind, which picks its form config and fields. */
export type LineType = "labor" | "material" | "placeholder";

export function useAddEditItemModalFormState(
  type: LineType,
  readOnly: boolean,
  // Creating new fields will
  initialTliData: TliData | undefined,
) {
  // We use slightly different formConfigs for material/placeholder/labor
  const formConfig = useMemo(
    () => ({ material: addMaterialItemConfig, placeholder: addPlaceholderItemConfig, labor: addLaborItemConfig })[type],
    [type],
  );

  return useFormState({
    config: formConfig,
    readOnly: readOnly,
    init: {
      input: initialTliData,
      ifUndefined: newAddEditItemFormDefaults,
      map: (data) => {
        return {
          id: data.id,
          action: data.action?.code ?? TakeoffLineItemAction.Add,
          costType: data.costType,
          itemId: data.item?.id,
          item: data.item,
          name: data.name,
          materialVariant: data.materialVariant,
          materialVariantId: data.materialVariant?.id,
          quantity: data.quantity ?? 0,
          unitOfMeasure: data.unitOfMeasure,
          unitOfMeasureId: data.unitOfMeasure?.id,
          // Treat `Kitchen > Sink` as just `Kitchen`
          locationId: data.location.nonScopeLocation.id,
          location: data.location.nonScopeLocation,
          optionIds: data.options?.map((o) => o.id) ?? [],
          taskId: data.task?.id,
        };
      },
    },
  });
}

export async function saveAddEditItem(params: {
  formState: FormObjectState<AddEditItemObjectState, TliData>;
  saveTli: (item: SaveTakeoffLineItemInput) => SaveTliResult;
  isPlaceholderItem?: boolean;
}) {
  const { formState, saveTli, isPlaceholderItem } = params;
  const result = await saveTli(toAddEditItemInput(formState, isPlaceholderItem));
  // Refresh the form from the line the server saved, so it's no longer dirty
  const saved = result?.data?.saveTakeoffLineItems.takeoffLineItems.first;
  if (saved) formState.update(saved);
  return result;
}

/** The line's changed fields as a save input. */
export function toAddEditItemInput(
  formState: ObjectState<AddEditItemObjectState>,
  isPlaceholderItem?: boolean,
): SaveTakeoffLineItemInput {
  // itemId does not need to be sent, since it is derived from materialVariantId or taskId
  const {
    itemId,
    item,
    materialVariant,
    unitOfMeasure,
    quantity,
    newLocationPath,
    // Pulling out location here so that it does not end up in rest
    location: changedLocation,
    locationId,
    ...rest
  } = formState.changedValue;

  // Fall back to formState if materialVariant isn't in changedValue (e.g. pre-filled from DP flow).
  // But if itemId changed, keep undefined so we send [] to clear stale MAVs.
  const materialVariantForMavs = itemId ? materialVariant : (materialVariant ?? formState.materialVariant.value);

  return {
    ...rest,
    ...(newLocationPath ? { locationPath: newLocationPath } : locationId ? { locationId } : {}),
    ...(formState.unitOfMeasure.value?.useQuantity !== false && quantity && { quantity }),
    ...(isPlaceholderItem && {
      // It's unclear why we always send itemId...
      itemId: itemId || formState.itemId.value,
      placeholderMaterialAttributeValues:
        // Existing TLI with no itemId/MAV changes: omit field so backend preserves the placeholder MV.
        // Detect MAV changes via materialVariant === undefined, not empty array — clearing all values sends [].
        formState.id.value && materialVariant === undefined && !itemId
          ? undefined
          : (materialVariantForMavs?.materialAttributeValues?.map(({ id, textValue, code, dimension }) => ({
              ...(id && { id }),
              ...(code && { code }),
              textValue,
              dimensionId: dimension.id,
            })) ?? []),
    }),
  };
}
